// ✅ /src/hooks/useCurrentUser.js
import { useEffect, useState, useContext } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useImpersonation } from "../context/ImpersonationContext";

// Role and company from the login token's claims (set by the backend), or null if it has none
export async function roleFromToken(firebaseUser, refresh = false) {
  const claims = (await firebaseUser.getIdTokenResult(refresh).catch(() => null))?.claims;
  if (claims?.hasbroCustomerId) return { role: "hasbro-customer", companyId: null };
  return typeof claims?.role === "string" ? { role: claims.role, companyId: claims.companyId || null } : null;
}

export default function useCurrentUser() {
  const { impersonation } = useImpersonation();

  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [userName, setUserName] = useState("User");
  const [features, setFeatures] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [enabledModules, setEnabledModules] = useState(null);
  const [themePreference, setInternalThemePreference] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setRole(null);
        setClientId(null);
        setFeatures(null);
        setUserPermissions(null);
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUser(firebaseUser);
          setRole(data.role || "unknown");
          setClientId(data.companyId || data.clientId || null);
          setUserName(data.name || firebaseUser.displayName || "User");
          setFeatures(data.features || {});
          setUserPermissions(data.userPermissions || []);
          setEnabledModules(data.enabledModules || null);
          setInternalThemePreference(data.themePreference || null);
        } else {
          console.error("❌ No Firestore user doc found for UID:", firebaseUser.uid);
        }
      } catch (err) {
        console.error("❌ Failed to fetch user info:", err);
        // Firestore unreachable (e.g. its daily read quota): the login token carries role and company
        const claims = await roleFromToken(firebaseUser);
        if (claims) {
          setUser(firebaseUser);
          setRole(claims.role);
          setClientId(claims.companyId);
          setUserName(firebaseUser.displayName || "User");
          setFeatures({});
          setUserPermissions([]);
        }
      }

      setLoading(false);
    });

    // 🕒 Safety Timeout: If Firebase never responds, stop loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 5000);

    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  const setThemePreference = async (newTheme) => {
    setInternalThemePreference(newTheme);
    if (user && newTheme) {
      try {
        await updateDoc(doc(db, "users", user.uid), {
          themePreference: newTheme
        });
      } catch (err) {
        console.error("❌ Failed to update theme preference in Firestore:", err);
      }
    }
  };

  // When impersonating, override identity fields with the target user's data
  if (impersonation) {
    return {
      user,
      role: impersonation.role,
      clientId: impersonation.companyId,
      userName: impersonation.name,
      features: impersonation.features,
      userPermissions: impersonation.userPermissions,
      enabledModules: impersonation.enabledModules,
      themePreference,
      setThemePreference,
      loading: false,
    };
  }

  return { user, role, clientId, userName, features, userPermissions, enabledModules, themePreference, setThemePreference, loading };
}
